import { Search, X } from "lucide-react";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// "/" keyboard shortcut: pressing / outside a text field focuses the first
// mounted SearchInput (the page-level one — panels mount later). One global
// listener over a mount-order registry, installed lazily on first mount.
const searchRegistry: RefObject<HTMLInputElement | null>[] = [];
let slashListenerInstalled = false;

function installSlashListener() {
  if (slashListenerInstalled) return;
  slashListenerInstalled = true;
  window.addEventListener("keydown", (e) => {
    if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
    const el = document.activeElement as HTMLElement | null;
    if (
      el &&
      (el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.tagName === "SELECT" ||
        el.isContentEditable)
    )
      return;
    const target = searchRegistry[0]?.current;
    if (!target) return;
    e.preventDefault();
    target.focus();
    target.select();
  });
}

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  onClear?: () => void;
  /**
   * Debounce (ms) before propagating typed input via onChange. Keeps the field
   * responsive while throttling expensive commits (e.g. URL/history writes that
   * the browser rate-limits). Clearing is always immediate. 0 = no debounce.
   */
  debounceMs?: number;
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  inputClassName,
  onClear,
  debounceMs = 250,
}: SearchInputProps) {
  const { t } = useTranslation();
  // Local mirror so typing stays instant even while the committed value lags.
  const [local, setLocal] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Sync when the controlled value changes from outside (reset, deep-link, etc.).
  // Render-phase adjust (React's documented pattern) instead of a setState effect.
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setLocal(value);
  }

  useEffect(() => () => clearTimeout(timer.current), []);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    installSlashListener();
    searchRegistry.push(inputRef);
    return () => {
      const i = searchRegistry.indexOf(inputRef);
      if (i >= 0) searchRegistry.splice(i, 1);
    };
  }, []);

  const commit = (next: string) => {
    setLocal(next);
    if (debounceMs <= 0) {
      onChange(next);
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(next), debounceMs);
  };

  const handleClear = () => {
    clearTimeout(timer.current);
    setLocal("");
    onChange("");
    onClear?.();
  };

  return (
    <div className={cn("relative", className)}>
      <Search
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
      />
      <input
        ref={inputRef}
        type="text"
        value={local}
        onChange={(e) => commit(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full font-mono text-base md:text-xs bg-card border border-border/30 rounded-md pl-8 pr-3 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors",
          local && "pr-8",
          inputClassName
        )}
      />
      {local && (
        <button
          aria-label={t("clear")}
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground transition-colors"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}
